import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ICompanyConfigFeature } from './company-config.feature.state';
import {
  getCompanyConfigAction,
  setSiteIDAction,
  setOrderFromSiteAction,
  clearQRScanDataAction,
  setIsAnonymousAction,
  setIsContrastColor,
} from './company-config.actions';
import {
  getCompanyConfig,
  getFailures,
  getIsLoading,
  getCompanyConfigIndex,
  getOrderFromSite,
  getSiteId,
  getIsContrastColor,
} from './company-config.selectors';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { CompanyConfigService } from '../services';
import { CompanyConfigType } from '../enum';

const GUEST_CHECKOUT_ALLOWED_PAYMENT_GATEWAYS = [1, 4, 5];

@Injectable()
export class CompanyConfigFacade {
  public isLoading$ = this.store.select(getIsLoading);
  public failures$ = this.store.select(getFailures);
  public companyConfig$ = this.store.select(getCompanyConfig).pipe(filter((data) => data !== null));
  public companyConfigIndex$ = this.store.select(getCompanyConfigIndex).pipe(filter((data) => data !== null));
  public isContrastColor$ = this.store.select(getIsContrastColor);
  // public isAnonymousBrowsing$ = this.store.select(getIsAnonymousBrowsing);
  public siteId$ = this.store.select(getSiteId);
  public isOrderFromSite$ = this.store.select(getOrderFromSite).pipe(
    map((orderFromSite) => {
      if (orderFromSite) {
        const data = orderFromSite.split('_');
        return data.length > 0 && (data[0] === '1' || data[0] === '2');
      }
      return false;
    })
  );

  public orderFromSiteValue$ = this.store.select(getOrderFromSite).pipe(
    map((orderFromSite) => {
      if (orderFromSite) {
        const data = orderFromSite.split('_');
        const check = data.length > 0 && (data[0] === '1' || data[0] === '2');
        return check ? data[0] : null;
      }
      return null;
    })
  );

  public seatingLocation$ = this.store.select(getOrderFromSite).pipe(
    map((orderFromSite) => {
      if (orderFromSite) {
        const data = orderFromSite.split('_');
        const resp = {
          isClickCollectAllowed: +data[0],
          seatingLocationID: +data[1],
          seatingLocationTableNo: +data[2],
        };
        const check = data.length > 0 && (data[0] === '1' || data[0] === '2');
        return check ? resp : null;
      }
      return null;
    })
  );

  public isAnonymousBrowsing$ = this.companyConfigIndex$.pipe(
    map((companyConfigIndex) => {
      return companyConfigIndex ? companyConfigIndex[CompanyConfigType.AnonymousBrowsing] : true;
    })
  );

  public isCCAllowed$ = this.companyConfigIndex$.pipe(
    map((companyConfigIndex) => {
      return companyConfigIndex[CompanyConfigType.AllowClickAndCollect];
    })
  );

  public checkoutAsGuestSharedAccountId$ = this.companyConfigIndex$.pipe(
    map((companyConfigIndex) => {
      return companyConfigIndex ? companyConfigIndex[CompanyConfigType.SoloCheckoutAsGuestSharedAccountId] : 0;
    })
  );

  public checkoutAsGuestPaymentGatewayAvaliable$ = this.companyConfigIndex$.pipe(
    map((companyConfigIndex) => {
      return (
        companyConfigIndex[CompanyConfigType.MyCardsPaymentCardsBoolean] &&
        GUEST_CHECKOUT_ALLOWED_PAYMENT_GATEWAYS.includes(companyConfigIndex[CompanyConfigType.MyCardsPaymentCardsBoolean])
      );
    })
  );

  public isCustomerInfoOptional$ = this.companyConfigIndex$.pipe(
    map((companyConfigIndex) => {
      return companyConfigIndex ? companyConfigIndex[CompanyConfigType.ChecoutAsGuestAllowAnonymous] : true;
    })
  );

  public isGoogleAndApplePayAllowed$ = this.companyConfigIndex$.pipe(
    map((companyConfigIndex) => {
      return companyConfigIndex ? companyConfigIndex[CompanyConfigType.AllowGoogleAndApplePay] : false;
    })
  );

  public companyConfigSubj = new Subject<any>();
  public anonymousBrowsing = new BehaviorSubject<boolean>(false);
  public anonymousBrowsing$ = this.anonymousBrowsing.asObservable();

  constructor(private store: Store<ICompanyConfigFeature>, public companyConfigService: CompanyConfigService) {}

  public getCompanyConfig(): void {
    this.store.dispatch(getCompanyConfigAction());
  }

  public setIsContrastColor(isContrastColor: boolean): void {
    this.store.dispatch(setIsContrastColor({ isContrastColor }));
  }

  public setSiteID(siteId: string): void {
    this.store.dispatch(setSiteIDAction({ siteId }));
  }

  public setOrderFromSite(orderFromSite: string): void {
    this.store.dispatch(setOrderFromSiteAction({ orderFromSite }));
  }

  public clearQRScanData(): void {
    this.store.dispatch(clearQRScanDataAction());
  }

  public setIsAnonymous(isAnonymous: boolean): void {
    this.store.dispatch(setIsAnonymousAction({ isAnonymous }));
  }

  public getIsAnonymous(): any {
    return this.companyConfigService.getCompanyConfig().pipe(
      map((companyConfig: any) => {
        let isAnonymous = false;
        for (const cc of companyConfig) {
          if (cc?.id === 270 && cc?.value === '1') {
            this.anonymousBrowsing.next(true);
            isAnonymous = true;
          }
        }
        return isAnonymous;
      })
    );
  }
}
